import {
  type BookingUnit,
  type GuestEmailRequirement,
  type RestaurantEvent,
  type ServiceFeePayload,
} from '@shared/types/restaurants';
import { wholeDollarsToCents } from '@shared/utils/currency';
import { api } from '../../api';
import {
  type EventAddOnFormItem,
  type EventFormData,
  isPersistableAddOnFormItem,
  isPersistableDisclaimerFormItem,
} from './form/RestaurantEventForm';

export const getRestaurantEvents = async (
  restaurantId: string,
): Promise<RestaurantEvent[]> => api.get(`/restaurants/${restaurantId}/events`);

export const getRestaurantEvent = async (
  restaurantId: string,
  eventId: string,
  signal?: AbortSignal,
): Promise<RestaurantEvent> =>
  api.get<RestaurantEvent>(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}`,
    {
      signal,
    },
  );

export type CreateRestaurantEventTierPayload = {
  name: string;
  description: string | null;
  price: number;
  capacity: number;
  floorPlanTableIds: string[];
};

export type CreateRestaurantEventPayload = {
  name: string;
  description?: string;
  location?: string | null;
  directions?: string | null;
  customConfirmationMessage?: string | null;
  includeTicketsLinkInConfirmation: boolean;
  bookingUnit: BookingUnit;
  guestEmailRequirement: GuestEmailRequirement;
  startDate: string;
  startTime: string;
  endTime: string;
  capacity: number;
  serviceChargePercentage?: number;
  imageKeys: string[];
  disclaimers: { title: string; description: string | null }[];
  tiers: CreateRestaurantEventTierPayload[];
  serviceFees?: ServiceFeePayload[];
  addOns?: RestaurantEventNestedAddOnPayload[];
};

export type RestaurantEventNestedAddOnPayload = {
  id?: string;
  name: string;
  description: string;
  price: number;
  imageKey?: string;
  tierIds: string[];
};

export function buildNestedAddOnsPayload(
  formAddOns: EventAddOnFormItem[] | undefined,
): RestaurantEventNestedAddOnPayload[] {
  return (formAddOns ?? []).filter(isPersistableAddOnFormItem).map((addOn) => ({
    ...(addOn.id ? { id: addOn.id } : {}),
    name: addOn.name,
    description: addOn.description ?? '',
    price: wholeDollarsToCents(Number.parseFloat(addOn.price) || 0),
    imageKey: addOn.imageKey,
    tierIds: addOn.tierIds ?? [],
  }));
}

export const createRestaurantEvent = async (
  restaurantId: string,
  payload: CreateRestaurantEventPayload,
): Promise<RestaurantEvent> =>
  api.post<RestaurantEvent>(
    `/restaurants/${restaurantId}/restaurant-events`,
    payload,
  );

export type UpdateRestaurantEventTierPayload =
  CreateRestaurantEventTierPayload & { id?: string };

export type UpdateRestaurantEventPayload = Omit<
  CreateRestaurantEventPayload,
  'tiers'
> & {
  ignoreWarnings: boolean;
  shiftId: string;
  tiers: UpdateRestaurantEventTierPayload[];
};

export const updateRestaurantEvent = async (
  restaurantId: string,
  eventId: string,
  payload: UpdateRestaurantEventPayload,
) =>
  api.put<RestaurantEvent | { warnings: string[] }>(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}`,
    payload,
  );

export const deleteRestaurantEvent = async (
  restaurantId: string,
  eventId: string,
): Promise<void> => {
  return api.delete<void>(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}`,
  );
};

export interface RestaurantEventImageUploadUrl {
  signedUploadImageUrl: string;
  signedGetImageUrl: string;
  objectKey: string;
}

export const getEventImageUploadUrl = async (restaurantId: string) =>
  api.get<RestaurantEventImageUploadUrl>(
    `/restaurants/${restaurantId}/events/upload-url`,
  );

export interface AddOnPayload {
  name: string;
  description: string;
  price: number;
  imageKey: string;
  tierIds: string[];
}

export const createRestaurantEventAddOn = async (
  restaurantId: string,
  eventId: string,
  payload: AddOnPayload,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}/add-ons`,
    payload,
  );

export const updateRestaurantEventAddOn = async (
  restaurantId: string,
  eventId: string,
  addOnId: string,
  payload: AddOnPayload,
): Promise<void> =>
  api.put(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}/add-ons/${addOnId}`,
    payload,
  );

export const deleteRestaurantEventAddOn = async (
  restaurantId: string,
  eventId: string,
  addOnId: string,
): Promise<void> =>
  api.delete(
    `/restaurants/${restaurantId}/restaurant-events/${eventId}/add-ons/${addOnId}`,
  );

export function toApiTime(time: string): string {
  return time && time.length === 5 ? `${time}:00` : time;
}

export function buildCreateRestaurantEventPayload(
  values: EventFormData,
): CreateRestaurantEventPayload {
  const ticketTiers = values.ticketTiers ?? [];
  const capacity = ticketTiers.reduce(
    (sum, tier) => sum + (Number(tier.capacity) || 0),
    0,
  );

  return {
    name: values.name,
    description: values.description || undefined,
    location: values.location || null,
    directions: values.directions || null,
    customConfirmationMessage: values.customConfirmationMessage || null,
    includeTicketsLinkInConfirmation: values.includeTicketsLinkInConfirmation,
    bookingUnit: values.bookingUnit,
    guestEmailRequirement: values.guestEmailRequirement,
    startDate: values.startDate,
    startTime: toApiTime(values.startTime),
    endTime: toApiTime(values.endTime),
    capacity,
    serviceChargePercentage:
      values.serviceChargePercentage === '' ||
      values.serviceChargePercentage === undefined
        ? 0
        : Number(values.serviceChargePercentage),
    imageKeys: (values.images ?? [])
      .map((image) => image.imageKey)
      .filter(Boolean),
    disclaimers: (values.disclaimers ?? [])
      .filter(isPersistableDisclaimerFormItem)
      .map((disclaimer) => ({
        title: disclaimer.title.trim(),
        description: disclaimer.description || null,
      })),
    tiers: ticketTiers.map((tier) => ({
      name: tier.name,
      description: tier.description?.trim() || null,
      price: wholeDollarsToCents(Number.parseFloat(tier.price) || 0),
      capacity: Number(tier.capacity) || 0,
      floorPlanTableIds: tier.floorPlanTableIds ?? [],
    })),
    addOns: buildNestedAddOnsPayload(values.addOns),
  };
}

export function buildUpdateRestaurantEventPayload(
  values: EventFormData,
  shiftId: string,
  existingTiers: RestaurantEvent['tiers'],
): UpdateRestaurantEventPayload {
  const ticketTiers = values.ticketTiers ?? [];
  const base = buildCreateRestaurantEventPayload(values);

  return {
    ignoreWarnings: true,
    shiftId,
    name: base.name,
    description: base.description ?? '',
    location: base.location,
    directions: base.directions,
    customConfirmationMessage: base.customConfirmationMessage,
    includeTicketsLinkInConfirmation: base.includeTicketsLinkInConfirmation,
    bookingUnit: base.bookingUnit,
    guestEmailRequirement: base.guestEmailRequirement,
    startDate: base.startDate,
    startTime: base.startTime,
    endTime: base.endTime,
    capacity: base.capacity,
    serviceChargePercentage: base.serviceChargePercentage,
    imageKeys: base.imageKeys,
    disclaimers: base.disclaimers,
    tiers: ticketTiers.map((tier, index) => ({
      id: existingTiers?.[index]?.id,
      name: tier.name,
      description: tier.description?.trim() || null,
      price: wholeDollarsToCents(Number.parseFloat(tier.price) || 0),
      capacity: Number(tier.capacity) || 0,
      floorPlanTableIds: tier.floorPlanTableIds ?? [],
    })),
    addOns: buildNestedAddOnsPayload(values.addOns),
  };
}
